import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { FormValidators } from '../../../core/forms/form-validators';
import { SmartForm } from '../../../core/forms/smart-form';
import { SmartFormService } from '../../../core/forms/smart-form.service';
import { AiAgentsService, AiConnectionScope, AiModelConnection, AiModelConnectionPayload, AiProvider } from '../../../core/services/ai-agents';

@Component({
  selector: 'app-ai-model-connection-form',
  standalone: false,
  templateUrl: './ai-model-connection-form.html',
  styleUrl: './ai-model-connection-form.scss',
})
export class AiModelConnectionForm implements OnInit, OnChanges {
  @Input({ required: true }) scope: AiConnectionScope = 'personal';
  @Input() record: AiModelConnection | null = null;
  @Output() saved = new EventEmitter<AiModelConnection>();
  @Output() cancelled = new EventEmitter<void>();

  providers: AiProvider[] = [];
  form!: SmartForm<Record<string, unknown>>;

  constructor(
    private readonly api: AiAgentsService,
    private readonly forms: SmartFormService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.form = this.createForm();
    this.api.providers().subscribe({ next: providers => { this.providers = providers; this.applyProviderRequirements(); this.cdr.detectChanges(); } });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['record'] && this.form) this.form = this.createForm();
  }

  providerChanged(): void {
    this.applyProviderRequirements();
  }

  get selectedProvider(): AiProvider | undefined { return this.providers.find(item => item.id === this.form?.control('provider')?.value); }
  submit(): void { this.form.submit(); }
  error(field: string): string | null { return this.form.getError(field); }
  invalid(field: string): boolean { return this.form.isControlInvalid(field); }

  private createForm(): SmartForm<Record<string, unknown>> {
    const record = this.record;
    return this.forms.create<Record<string, unknown>>({
      name: [record?.name ?? '', [FormValidators.required('a connection name'), FormValidators.maxLength(120, 'Connection name')]],
      provider: [record?.provider ?? 'openai', [FormValidators.required('a provider')]],
      base_url: [record?.base_url ?? 'https://api.openai.com/v1', [FormValidators.required('an API base URL'), FormValidators.maxLength(1000, 'API base URL')]],
      api_key: ['', record ? [] : [FormValidators.required('an API key or bearer token')]],
      region: [record?.region ?? '', [FormValidators.maxLength(240, 'Region')]],
      project_id: [record?.project_id ?? '', [FormValidators.maxLength(240, 'Project ID')]],
      priority: [record?.priority ?? 100, [FormValidators.minValue(0, 'Priority'), FormValidators.maxValue(10000, 'Priority')]],
      max_output_tokens: [record?.max_output_tokens ?? 4096, [FormValidators.minValue(128, 'Output token limit'), FormValidators.maxValue(16384, 'Output token limit')]],
      active: [record?.active ?? true, []],
    }, value => {
      const payload = this.payload(value);
      const save: Observable<AiModelConnection> = record
        ? this.api.updateConnection(this.scope, record.id, payload)
        : this.api.testConnectionPayload(this.scope, payload).pipe(switchMap(() => this.api.createConnection(this.scope, payload)));
      return save;
    }, response => this.saved.emit(response), undefined, {
      successMessage: record ? 'AI model connection updated.' : 'AI model connection created.',
      invalidMessage: 'Fix the highlighted connection fields.',
    });
  }

  private payload(value: Record<string, unknown>): AiModelConnectionPayload {
    const optional = (key: string): string | null => String(value[key] ?? '').trim() || null;
    return {
      name: String(value['name'] ?? '').trim(), provider: String(value['provider'] ?? ''),
      base_url: String(value['base_url'] ?? '').trim(),
      api_key: optional('api_key'), region: optional('region'), project_id: optional('project_id'),
      priority: Number(value['priority']), max_output_tokens: Number(value['max_output_tokens']),
      active: value['active'] === true,
    };
  }

  private applyProviderRequirements(): void {
    if (!this.form) return;
    const provider = this.selectedProvider;
    const base = this.form.control('base_url');
    if (provider && (!base?.value || !base.dirty)) base?.setValue(provider.default_url, { emitEvent: false });
    this.form.control('api_key')?.setValidators(!this.record && provider?.api_key_required ? [FormValidators.required('an API key or bearer token')] : []);
    this.form.control('base_url')?.setValidators([FormValidators.required('an API base URL'), FormValidators.maxLength(1000, 'API base URL')]);
    this.form.control('region')?.setValidators(provider?.region_required ? [FormValidators.required('a cloud region'), FormValidators.maxLength(240, 'Region')] : [FormValidators.maxLength(240, 'Region')]);
    this.form.control('project_id')?.setValidators(provider?.project_id_required ? [FormValidators.required('a cloud project ID'), FormValidators.maxLength(240, 'Project ID')] : [FormValidators.maxLength(240, 'Project ID')]);
    ['api_key', 'base_url', 'region', 'project_id'].forEach(field => this.form.control(field)?.updateValueAndValidity({ emitEvent: false }));
  }

}
